import React, { lazy } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Lazy resolvers for the two copilot-ui components the shell chrome mounts. Kept
// out of AppShell so that file stays under the view-LOC ceiling and the
// federated-remote resolution concern lives in one place. Both are fully typed
// (no `any`): the copilot-ui module surface is modelled below.
// ─────────────────────────────────────────────────────────────────────────────

// ── CopilotPanel (the summonable panel) ──────────────────────────────────────
// The remote may expose the panel as a named `CopilotPanel`, via an
// `overlayComponents.CopilotPanel` loader, or as a chat view on the default
// manifest. We try each shape so a copilot-ui change doesn't silently degrade
// the shell to the bottom-bar fallback.
type CopilotPanelProps = { isOpen: boolean; onToggle: (open: boolean) => void };
type CopilotPanelComponent = React.ComponentType<CopilotPanelProps>;
type CopilotView = { viewId?: string; loader?: () => Promise<{ default: CopilotPanelComponent }> };
type CopilotPanelModule = {
 CopilotPanel?: CopilotPanelComponent;
 overlayComponents?: { CopilotPanel?: () => Promise<{ default?: CopilotPanelComponent } | CopilotPanelComponent> };
 default?: { views?: CopilotView[] };
};

export const CopilotPanel = lazy<CopilotPanelComponent>(() =>
 // Compiled-in first-party module via shell rspack alias (copilot-ui is a lazy
 // chunk, not a federated remote). The loaded bundle arrives opaque (`unknown`)
 // and is narrowed to the modelled copilot-ui surface at this seam.
 import('@tyndo/copilot-ui/module').then(async (imported: unknown) => {
 const mod = imported as CopilotPanelModule;
 if (mod.CopilotPanel) return { default: mod.CopilotPanel };
 const namedLoader = mod.overlayComponents?.CopilotPanel;
 if (typeof namedLoader === 'function') {
 const resolved = await namedLoader();
 const comp = (resolved as { default?: CopilotPanelComponent }).default
 ?? (resolved as CopilotPanelComponent);
 return { default: comp };
 }
 const views = mod.default?.views ?? [];
 const chatView = views.find((v) => v.viewId?.includes('chat') || v.viewId?.includes('copilot'));
 if (chatView?.loader) return chatView.loader();
 const loading: CopilotPanelComponent = () => <div className="p-4 text-muted-foreground">Copilot module loading…</div>;
 return { default: loading };
 }).catch(() => {
 const unavailable: CopilotPanelComponent = () => <div className="p-4 text-muted-foreground">Copilot unavailable</div>;
 return { default: unavailable };
 })
);

// ── AmbientCopilotRail (F0-9, the persistent woven-in rail) ───────────────────
// copilot-ui exposes it as a named export and via its widgets loader-map; we try
// each shape so a copilot-ui change does not silently break the mount. The rail
// self-gates on the `copilot:read` permission and renders a quiet empty state
// when there is nothing grounded to surface, so an always-mounted rail stays
// honest and unobtrusive.
type AmbientRailProps = { moduleId: string; defaultCollapsed?: boolean };
type AmbientRailComponent = React.ComponentType<AmbientRailProps>;
type AmbientRailModule = {
 AmbientCopilotRail?: AmbientRailComponent;
 widgets?: Record<string, () => Promise<{ default?: AmbientRailComponent } | AmbientRailComponent>>;
};
const emptyRail: AmbientRailComponent = () => null;

export const AmbientCopilotRail = lazy<AmbientRailComponent>(() =>
 // Compiled-in first-party module via shell rspack alias; narrowed from
 // `unknown` to the modelled rail surface at this seam.
 import('@tyndo/copilot-ui/module').then(async (imported: unknown) => {
 const mod = imported as AmbientRailModule;
 if (mod.AmbientCopilotRail) return { default: mod.AmbientCopilotRail };
 const namedLoader = mod.widgets?.AmbientCopilotRail;
 if (typeof namedLoader === 'function') {
 const resolved = await namedLoader();
 const comp = (resolved as { default?: AmbientRailComponent }).default
 ?? (resolved as AmbientRailComponent);
 return { default: comp };
 }
 return { default: emptyRail };
 }).catch(() => ({ default: emptyRail }))
);
